<template>
    <div
        v-if="is404"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>{{ $t('messages.brand.noBrand', { id: brandId }) }}</p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="brand.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors, validate }"
                        name="countryType"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('countryType') }} <b class="text-danger">*</b></label>

                            <FormSelect
                                v-model="brand.countryType"
                                :options="countryTypes"
                                :placeholder="`${$t('select')} ${$t('countryType')}`"
                                closeOnSelect
                                :disabled="isDisabledCountryType"
                                trackBy="id"
                                label="name"
                                @close="validate"
                                @input="onChangeCountryType"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('state.states') }}</label>

                        <FormSelect
                            v-model="brand.states"
                            :disabled="brand.statesExclude && brand.statesExclude.length > 0"
                            :options="states"
                            :placeholder="`${$t('select')} ${$t('state.states')}`"
                            allowEmpty
                            label="name"
                            multiple
                            searchable
                            trackBy="id"
                        />

                    </div>
                </div>
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('state.statesExclude') }}</label>

                        <FormSelect
                            v-model="brand.statesExclude"
                            :disabled="brand.states && brand.states.length > 0"
                            :options="states"
                            :placeholder="`${$t('select')} ${$t('state.statesExclude')}`"
                            allowEmpty
                            label="name"
                            multiple
                            searchable
                            trackBy="id"
                        />

                    </div>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('gender.genders') }}</label>

                        <FormSelect
                            v-model="brand.genders"
                            :options="genders"
                            :placeholder="`${$t('select')} ${$t('gender.genders')}`"
                            allowEmpty
                            label="name"
                            multiple
                            searchable
                            trackBy="id"
                        />

                    </div>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('provider.providers') }}</label>

                        <FormSelect
                            v-model="brand.providers"
                            :options="providers"
                            :placeholder="`${$t('select')} ${$t('provider.providers')}`"
                            multiple
                            searchable
                            allowEmpty
                            :disabled="isDisabledProviders"
                            trackBy="id"
                            label="name"
                            @input="onChangeProvider"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="verticals"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('vertical.verticals') }}</label>

                            <FormSelect
                                v-model="brand.verticals"
                                :options="verticals"
                                :placeholder="`${$t('select')} ${$t('vertical.verticals')}`"
                                :disabled="isDisabledVerticals"
                                :loading="isLoadingVericals"
                                multiple
                                searchable
                                allowEmpty
                                trackBy="id"
                                label="name"
                                @input="onChangeVerticals"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                            >
                                {{ errors[0] }}
                            </div>
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('api.apis') }}</label>

                        <FormSelect
                            v-model="brand.apis"
                            :options="apisFiltered"
                            :placeholder="`${$t('select')} ${$t('api.apis')}`"
                            :disabled="IsDisabledApis"
                            :loading="isLoadingApis"
                            multiple
                            searchable
                            allowEmpty
                            trackBy="id"
                            label="name"
                        />
                    </div>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('offer.offers') }}</label>

                        <FormSelect
                            v-model="brand.offers"
                            :options="offers"
                            :placeholder="`${$t('select')} ${$t('offer.offers')}`"
                            :disabled="isDisabledOffers"
                            multiple
                            searchable
                            allowEmpty
                            trackBy="id"
                            label="name"
                        />
                    </div>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-2">
                    <div class="form-group">
                        <label>{{ $t('manualDomains') }}</label>

                        <FormSwitcher
                            v-model="brand.domainsManual"
                            :disabled="isLoading || !!brand.domainsReusable"
                            @input="onChangeDomainsManual"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-2">
                    <div class="form-group">
                        <label>{{ $t('reusableDomains') }}</label>

                        <FormSwitcher
                            v-model="brand.domainsReusable"
                            :disabled="isLoading"
                            @input="onChangeDomainsReusable"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('reusableShortDomainTags') }}</label>

                        <FormSelect
                            v-model="brand.shortDomainsTags"
                            :options="shortDomainsTags"
                            :placeholder="`${$t('select')} ${$t('shortDomainTags')}`"
                            :disabled="isDisabledShortDomainsTags"
                            multiple
                            searchable
                            allowEmpty
                            trackBy="id"
                            label="name"
                            @input="onChangeShortDomainTags"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('shortDomains.shortDomains') }}</label>

                        <FormSelect
                            v-model="brand.shortDomains"
                            :options="shortDomains"
                            :placeholder="`${$t('select')} ${$t('shortDomains.shortDomains')}`"
                            :disabled="isDisabledShortDomains"
                            :loading="isLoadingShortDomains"
                            multiple
                            searchable
                            allowEmpty
                            trackBy="id"
                            label="name"
                        />
                    </div>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-2">
                    <div class="form-group">
                        <label>{{ $t('domainLimits') }}</label>

                        <FormSwitcher
                            v-model="brand.domainsLimits"
                            :disabled="isDisabledDomainsLimits"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-8">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isGlobalLoading || invalid"
                        @click="submitBrand"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isGlobalLoading"
                        @click="
                            $router.push({
                                name: 'brands.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>

                <div
                    v-if="isEdit"
                    class="col-xs-4 text-right"
                >
                    <button
                        class="btn btn-sm btn-danger btn-xs-block"
                        type="button"
                        title="Delete brand"
                        data-toggle="modal"
                        data-target="#delete-modal"
                        :disabled="isGlobalLoading"
                    >
                        {{ $t('delete') }}
                    </button>
                </div>
            </div>
        </div>

        <BrandsModalDelete
            :brand="brand"
            @deleted="onDeleted"
        />
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import BrandsModalDelete from '@/components/brands/BrandsModalDelete';
import FormSwitcher from '@/components/form/FormSwitcher';

export default {
    components: {
        BrandsModalDelete,
        FormSwitcher,
    },
    props: {
        brandId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            is404: false,
            isLoading: false,
            isLoadingVericals: false,
            isLoadingApis: false,
            isLoadingShortDomains: false,
            countryTypes: [],
            states: [],
            genders: [],
            providers: [],
            verticals: [],
            apis: [],
            offers: [],
            shortDomainsTags: [],
            shortDomains: [],
            brand: {
                name: '',
                countryType: null,
                states: [],
                statesExclude: [],
                genders: [],
                providers: [],
                verticals: [],
                apis: [],
                offers: [],
                domainsManual: 0,
                domainsReusable: 0,
                shortDomainsTags: [],
                shortDomains: [],
                domainsLimits: 0,
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.brandId;
        },
        isGlobalLoading() {
            return this.isLoading || this.isLoadingVericals || this.isLoadingApis || this.isLoadingShortDomains;
        },
        apisFiltered() {
            return this.apis
                .filter((api) => this.brand.providers.find((prov) => prov.id === api.provider_id))
                .map((api) => {
                    const result = {
                        id: api.id,
                        name: api.name,
                        provider_id: api.provider_id,
                    };

                    return result;
                })
                .sort((a, b) => {
                    return a.name < b.name ? -1 : 1;
                });
        },
        isDisabledCountryType() {
            return !!(this.isLoading || !this.countryTypes.length);
        },
        isDisabledProviders() {
            return !!(this.isLoading || !this.providers.length);
        },
        isDisabledVerticals() {
            return !!(this.isLoading || this.isLoadingVericals || !this.verticals.length);
        },
        IsDisabledApis() {
            return !!(this.isLoading || this.isLoadingApis || !this.apisFiltered.length || (this.verticals.length && this.brand.verticals && !this.brand.verticals.length));
        },
        isDisabledOffers() {
            return !!(this.isLoading || !this.offers.length);
        },
        isDisabledShortDomainsTags() {
            return !!(this.isLoading || !this.shortDomainsTags.length || !this.brand.domainsReusable);
        },
        isDisabledShortDomains() {
            return !!(this.isLoading || this.isLoadingShortDomains || !this.shortDomains.length || !!(!this.brand.domainsManual && !this.brand.domainsReusable));
        },
        isDisabledDomainsLimits() {
            return !!(this.isLoading || (!this.brand.domainsManual && !this.brand.domainsReusable) || this.brand.shortDomains.length < 2);
        },
    },
    async created() {
        this.isLoading = true;

        await this.fetchCommon();

        if (this.isEdit) {
            await this.fetchBrand();
        }

        this.isLoading = false;
        this.$emit('loaded', this.brand);
    },
    methods: {
        async fetchBrand() {
            try {
                this.isLoading = true;

                const {
                    data: { brand },
                } = await axios.get(`/brands/data/brand`, {
                    params: {
                        id: this.brandId,
                    },
                });

                brand.countryType = this.countryTypes.find((e) => e.id === brand.countryType);

                this.brand = brand;

                await this.fetchVerticals();
                await this.fetchApis();
                await this.fetchShortDomains();
            } catch (error) {
                this.is404 = error.response.status === 404;

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            }
        },
        async fetchCommon() {
            try {
                const {
                    data: { countryTypes, countryUSA, providers, offers, shortDomainsTags, states, genders },
                } = await axios.get(`/brands/data/common`);

                this.countryUSA = countryUSA;
                this.countryTypes = countryTypes || [];
                this.providers = providers || [];
                this.offers = offers || [];
                this.shortDomainsTags = shortDomainsTags || [];
                this.states = states || [];
                this.genders = genders || [];

                if (!this.brand.countryType) {
                    this.brand.countryType = this.countryTypes.find((e) => e.id === this.countryUSA);
                }
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            }
        },
        async fetchVerticals() {
            try {
                this.isLoadingVericals = true;
                this.isLoadingApis = true;

                const { data: verticals } =
                    this.brand.providers && this.brand.providers.length
                        ? await axios.get(`/apis/verticals/by-provider`, {
                              params: {
                                  providers: this.brand.providers.map(({ id }) => id).join(','),
                              },
                          })
                        : { data: [] };

                this.brand.verticals = this.brand.verticals.filter((b) => {
                    return verticals.find((v) => v.id === b.id);
                });

                this.verticals = verticals;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingVericals = false;
                this.isLoadingApis = false;
            }
        },
        async fetchApis() {
            try {
                this.isLoadingApis = true;

                const {
                    data: { apis },
                } =
                    this.brand.providers && this.brand.providers.length
                        ? await axios.get(`/campaigns/collect/apis`, {
                              params: {
                                  countryType: this.brand.countryType.id || null,
                                  providers: this.brand.providers.map((e) => e.id).join(','),
                                  verticals: this.brand.verticals.map(({ id }) => id).join(','),
                              },
                          })
                        : { data: { apis: [] } };

                this.brand.apis = this.brand.apis.filter((b) => {
                    return apis.find((a) => a.id === b.id);
                });

                this.apis = apis;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingApis = false;
            }
        },
        async fetchShortDomains() {
            try {
                this.isLoadingShortDomains = true;

                const {
                    data: { shortDomains },
                } = await axios.post(`/campaigns/collect/short-domains`, {
                    countryType: this.brand.countryType.id,
                    domainsReusable: this.brand.domainsReusable,
                    tags: this.brand.shortDomainsTags.map(({ id }) => id),
                });

                this.brand.shortDomains = this.brand.shortDomains.filter((b) => {
                    return shortDomains.find((v) => v.id === b.id);
                });

                if (shortDomains.length < 2) {
                    this.brand.domainsLimits = 0;
                }

                this.shortDomains = shortDomains || [];
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingShortDomains = false;
            }
        },
        async submitBrand() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const {
                    countryType,
                    providers,
                    verticals,
                    apis,
                    offers,
                    shortDomainsTags,
                    shortDomains,
                    states,
                    statesExclude,
                    genders,
                    ...brandData
                } = this.brand;

                const brand = {
                    ...brandData,
                    countryType: countryType.id,
                    providers: providers.map((e) => e.id),
                    verticals: verticals.map((e) => e.id),
                    apis: apis.map((e) => e.id),
                    offers: offers.map((e) => e.id),
                    shortDomainsTags: shortDomainsTags.map((e) => e.id),
                    shortDomains: shortDomains.map((e) => e.id),
                    states: states.map((e) => e.id),
                    statesExclude: statesExclude.map((e) => e.id),
                    genders: genders.map((e) => e.id),
                };

                const {
                    data: { action },
                } = await axios({
                    method: this.isEdit ? 'PUT' : 'POST',
                    url: '/brands/data/brand',
                    data: brand,
                });

                if (action === 'created') {
                    this.onCreated(brand.name);
                } else if (action === 'updated') {
                    this.onUpdated(brand.id, brand.name);
                }
            } catch (error) {
                if (error && error.response) {
                    const {
                        data: { errors, message },
                    } = error.response;

                    if (errors) {
                        this.$refs.observer.setErrors(errors);
                    }

                    EventBus.$emit('alertData', {
                        class: 'alert-danger',
                        message,
                        errors,
                    });
                }
            } finally {
                this.isLoading = false;
            }
        },
        async onDeleted() {
            return await this.$router.replace({
                name: 'brands.list',
            });
        },
        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'brands.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.brand.updated', { name }),
            });
        },
        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'brands.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.brand.created', { name }),
            });
        },
        async onChangeCountryType() {
            await this.fetchApis();
            await this.fetchShortDomains();
        },
        async onChangeProvider() {
            await this.fetchVerticals();
            await this.fetchApis();
        },
        onChangeVerticals() {
            if (this.verticals.length && !this.brand.verticals.length) {
                this.brand.apis = [];
            }

            this.fetchApis();
        },
        onChangeShortDomainTags() {
            this.fetchShortDomains();
        },
        async onChangeDomainsManual(value) {
            switch (value) {
                case 1:
                    this.fetchShortDomains();
                    break;
                case 0:
                    this.brand.shortDomains = [];
                    this.brand.shortDomainsTags = [];
                    this.brand.domainsLimits = 0;
                    break;
            }
        },
        async onChangeDomainsReusable(value) {
            switch (value) {
                case 1:
                    this.brand.domainsManual = 1;
                    this.fetchShortDomains();
                    break;
                case 0:
                    this.brand.domainsManual = 0;
                    this.brand.shortDomainsTags = [];
                    this.brand.shortDomains = [];
                    this.brand.domainsLimits = 0;
                    break;
            }
        },
    },
};
</script>
